import React, { useEffect, useRef, useState } from "react";

import { CircularProgress, Tooltip } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { createGenerateClassName, StylesProvider } from "@mui/styles";
import ShipeeziNewGrid, { GeneralActions, Icons as GridIcons, RowActionsExtra } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { useHistory } from "react-router-dom";

import Button, { Color } from "../../../../../../components/Form/Button";
import BadgeStatus from "../../../../../../components/Generic/BadgeStatus";
import RowActionRoutePlanner from "../../../../../../components/RowActionRoutePlanner";
import { PRIVATE_ROUTES } from "../../../../../../constants";
import { useAppState } from "../../../../../../context/StateContext";
import { Fleet } from "../../../../../../models/fleet";
import {
  Filters,
  Row,
  Searches,
  ShipeeziGridColumn,
  Sort,
  Sorts,
} from "../../../../../../models/shipeezi.table";
import { EquipmentOwnership, EquipmentType, Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import { getValueOrDefault } from "../../../../../../util/shipeezi.table";
import { FleetShowDrawer } from "./FleetShowDrawer";
import { FleetShowDrawerHandles } from "./FleetShowDrawer/FleetShowDrawer";
import useAction from "./hooks/useActions";
import useRoutePlannerAction from "./hooks/useRoutePlannerActions";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

const generateClassName = createGenerateClassName({
  productionPrefix: "fl-",
  seed: "fleet-page",
});

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
    changeStatus: boolean,
  }
}

const FleetPage = (props: Props) => {
  const {permission} = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { getAllFleet, countFleet, useUpdateFleet } = useAction();
  const { useAddToRoutePlanner, useRemoveFromRoutePlanner } = useRoutePlannerAction();
  const history = useHistory();

  const {
    mutate: addToRoutePlanner,
    isLoading: addingToRoutePlanner,
    variables: fleetToAdd,
  } = useAddToRoutePlanner();

  const {
    mutate: removeFromRoutePlanner,
    isLoading: removingFromRoutePlanner,
    variables: fleetIdToRemove,
  } = useRemoveFromRoutePlanner();

  const [fleets, setFleets] = useState<Fleet[]>([]);

  const [rows, setRows] = useState<Row[]>([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const fleetShowDrawerRef = useRef<FleetShowDrawerHandles>(null);

  const { refetch, isFetching, data } = getAllFleet(page, pageSize, searches, filters, sorting);
  const { refetch: refetchFleetTotal, data: totalData } = countFleet(filters, searches);

  const refetchListAndCount = () => {
    refetch();
    refetchFleetTotal();
  };

  const {
    mutate: updateFleet,
    isLoading: isUpdatingFleet,
    variables,
  } = useUpdateFleet(refetchListAndCount);

  useEffect(() => {
    if (data) {
      setFleets(data);
    }
  }, [data]);

  useEffect(() => {
    setTotal(totalData || 0);
  }, [totalData]);

  const manageEquipment = (isView: boolean, id?: number) => {
    const pathname = id ? `${PRIVATE_ROUTES.EQUIPMENT_MNT}/${id}` : PRIVATE_ROUTES.EQUIPMENT_MNT;

    history.push({
      pathname,
      state: isView ? { viewOnly: isView } : undefined,
    });
  };

  const createFleet = () => {
    manageEquipment(false, undefined);
  };
  const editFleet = (index: number) => {
    const data = fleets.find((fleet) => Number(fleet.id) === Number(index));

    if (data) {
      manageEquipment(false, data.id);
    }
  };

  const viewFleet = (index: number) => {
    const data = fleets.find((fleet) => Number(fleet.id) === Number(index));

    return (
      <Tooltip title="View" placement="top">
        <div>
          <FiEye
            size={18}
            color="#475467"
            className={classes.button}
            onClick={() => {
              if (!data) return;
              fleetShowDrawerRef.current?.open(data);
              // manageEquipment(true, data.id);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const viewChangeStatus = (fleetId: number) => {
    const fleetToUpdate = fleets.find((fleet) => Number(fleet.id) === Number(fleetId));
    const isFleetOutOfService = fleetToUpdate?.outOfService;

    return (
      <>
        {isUpdatingFleet && variables?.id === fleetId ? (
          <CircularProgress className={classes.circularProgress} size={20} />
        ) : (
          <Tooltip title="Change Status" placement="top">
            <div>
              <GridIcons.status
                data-cy="action-change"
                color={`${isFleetOutOfService ? "#00000042" : "#475467"}`}
                className={`${!isFleetOutOfService && classes.button}`}
                onClick={() => {
                  if (fleetToUpdate && !isFleetOutOfService) {
                    updateFleet({
                      id: Number(fleetId),
                      fleet: {
                        ...fleetToUpdate,
                        status:
                          fleetToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
                      },
                    });
                  }
                }}
              />
            </div>
          </Tooltip>
        )}
      </>
    );
  };

  const handleAddToRoutePlanner = (fleet?: Fleet) => {
    if (!fleet) return;

    addToRoutePlanner(fleet);
  };

  const handleRemoveFromRoutePlanner = (id?: number) => {
    if (!id) return;

    removeFromRoutePlanner(id);
  };

  const renderRoutePlannerAction = (index: number) => {
    const fleetToUpdate = fleets.find((fleet) => Number(fleet.id) === Number(index));

    return (
      <RowActionRoutePlanner
        uuid={fleetToUpdate?.externalUuid}
        loading={
          (fleetToAdd?.id === fleetToUpdate?.id && addingToRoutePlanner) ||
          (fleetIdToRemove === fleetToUpdate?.id && removingFromRoutePlanner)
        }
        add={() => handleAddToRoutePlanner(fleetToUpdate)}
        remove={() => handleRemoveFromRoutePlanner(fleetToUpdate?.id)}
      />
    );
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const handleSaveFleetSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, fleets);

    setRows([...rowsSelected]);
  };

  const columns: ShipeeziGridColumn<Fleet>[] = [
    {
      name: "equipmentType",
      title: translate("type"),
      hideSearch: true,
      formatText: (value) => getValueOrDefault(value),
    },
    {
      name: "description",
      title: translate("description"),
      formatText: (value) => getValueOrDefault(value),
    },
    {
      name: "fleetNumber",
      title: translate("fleetNumber"),
      formatText: (value: Fleet["fleetNumber"]) => value ?? "-",
    },
    {
      name: "ownership",
      title: translate("belongsTo"),
      hideSearch: true,
      formatText: (value) => getValueOrDefault(value),
    },
    {
      name: "condition",
      title: translate("condition"),
      formatText: (value) => getValueOrDefault(value),
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (status: string) => (
        <BadgeStatus value={status} className={classes.status} />
      ),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("type"),
          column: "equipmentType",
          options: [EquipmentType.FLEET, EquipmentType.GENERAL],
        },
        {
          name: translate("belongsTo"),
          column: "ownership",
          options: [
            EquipmentOwnership.OWN,
            EquipmentOwnership.RENTAL,
            EquipmentOwnership.SUBCONTRACTOR,
            EquipmentOwnership.LEASED,
          ],
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: permission && isShowAction(currentUser, permission.create)
      ? (
        <Button customColor={Color.SECONDARY_DARK} startIcon={<AddIcon />} onClick={createFleet}>
          <span className={classes.newRecordText}>{translate("newEquipment")}</span>
        </Button>
      )
      : undefined,
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [{
      title: "View",
      key: "handleView",
      buttonAndAction: viewFleet
    }];

    if (permission && isShowAction(currentUser, permission.changeStatus)) {
      actions?.push({
        title: "Change Status",
        key: "handleChangeStatus",
        buttonAndAction: viewChangeStatus,
      });
    }

    if (currentUser?.company?.useRoutePlanner) {
      actions?.push({
        title: translate("routePlanner"),
        key: "handleRoutePlanner",
        buttonAndAction: renderRoutePlannerAction,
      })
    }

    return actions;
  }

  return (
    <StylesProvider generateClassName={generateClassName}>
      <div data-cy="fleetListTable">
        <ShipeeziNewGrid
          data={fleets}
          columns={columns}
          totalItems={total}
          loadingData={isFetching}
          emptyData={isFetching ? "Loading..." : translate("noInformationToBeShown")}
          pageSize={pageSize}
          pageSizeList={[20, 40, 60, 100]}
          currentPage={page}
          onChangePageSize={setPageSize}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          showSort={false}
          rowActions={{
            orderBy: ["handleEdit", "handleView", "handleChangeStatus", "handleRoutePlanner"],
            handleEdit: permission && isShowAction(currentUser, permission.edit)
              ? (fleetId) => editFleet(Number(fleetId))
              : undefined,
          }}
          rowActionsExtra={handleRowActionsExtraByPermission()}
          positionActionsColumn="right"
          onSorting={handleSort}
          handleSelect={(rowsArr: number[]) => handleSaveFleetSelected(rowsArr)}
          generalActions={generalActions}
        />

        <FleetShowDrawer ref={fleetShowDrawerRef} />
        {/* <>
        {openModal && (
          <FleetModal
            open={openModal}
            hasError={hasError}
            fleetToUpdate={selectedFleet}
            isView={isView}
            isLoading={isUpdating || isCreating}
            closeModal={() => setOpenModal(false)}
            updateFleet={(fleet: Fleet) => {
              if (fleet.id) {
                updateFleet(
                  {
                    id: fleet.id,
                    fleet,
                  },
                  saveActions
                );
              }
            }}
            createFleet={(fleet: Fleet, addToRoutePlanner: boolean) => {
              createFleet({ fleet, addToRoutePlanner }, saveActions);
            }}
          />
        )}
      </> */}

        {/* <>
        {openConfirmation && (
          <ConfirmChangeStatus
            open={openConfirmation}
            rows={rows}
            hasErrors={hasError}
            isLoading={isUpdating}
            closeModal={() => setOpenConfirmation(false)}
            update={(rowId, status) => {
              if (rowId) {
                updateFleet({ id: rowId, fleet: { status } as Fleet }, saveActions);
                setClearSelection(true);
              }
            }}
          />
        )}
      </> */}
      </div>
    </StylesProvider>
  );
};

export default FleetPage;
